import React from 'react'
import { createPortal } from 'react-dom'
import { ValidationError, api } from '../../api/client'
import type { Expense } from '../../types/app'
import { useDialog } from '../../hooks/useDialog'
import Field from '../settings/Field'
import { saveErrorMessage } from '../settings/toast'
import { EXPENSE_CATEGORIES, toExpenseForm, toExpenseInput, type ExpenseForm } from './expenseForm'
import '../../components/Sheet.css'
import '../settings/Settings.css'

interface ExpenseSheetProps {
  expense: Expense | null
  today: string
  onClose: () => void
  onSaved: () => void
}

// ExpenseSheet adds or edits one expense: amount, category, date and an
// optional note. Editing an existing one also offers "Delete".
export default function ExpenseSheet({ expense, today, onClose, onSaved }: ExpenseSheetProps) {
  const [form, setForm] = React.useState<ExpenseForm>(() => toExpenseForm(expense, today))
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [confirmDelete, setConfirmDelete] = React.useState(false)

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  const set = <K extends keyof ExpenseForm>(key: K, value: ExpenseForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    const result = toExpenseInput(form, today)
    if ('errors' in result) {
      setErrors(result.errors)
      return
    }
    setBusy(true)
    setErrors({})
    setError('')
    try {
      if (expense) {
        await api.updateExpense(expense.id, result.input)
      } else {
        await api.createExpense(result.input)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors(err.fields)
      } else {
        setError(saveErrorMessage(err))
      }
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!expense) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.deleteExpense(expense.id)
      onSaved()
    } catch (err) {
      setError(saveErrorMessage(err))
      setBusy(false)
    }
  }

  const sheet = (
    <div className="sheet-overlay settings-sheet" onClick={() => { if (!busy) onClose() }}>
      <div ref={dialogRef}
        className="sheet-content"
        role="dialog"
        aria-modal="true"
        aria-label={expense ? 'Edit expense' : 'Add expense'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>{expense ? 'Edit expense' : 'New expense'}</h2>
        </div>

        <form className="settings-form sheet-form" onSubmit={save} noValidate>
          <Field id="expense-amount" label="Amount (£)" error={errors.amount_pence}>
            <input
              id="expense-amount"
              inputMode="decimal"
              placeholder="42.50"
              value={form.amount}
              onChange={(e) => set('amount', e.target.value)}
            />
          </Field>

          <Field id="expense-category" label="Category" error={errors.category}>
            <div className="chip-group" id="expense-category" role="group" aria-label="Category">
              {EXPENSE_CATEGORIES.map((category) => (
                <button
                  key={category.value}
                  type="button"
                  className="chip"
                  aria-pressed={form.category === category.value}
                  onClick={() => set('category', category.value)}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </Field>

          <Field id="expense-date" label="Date" error={errors.spent_on}>
            <input
              id="expense-date"
              type="date"
              max={today}
              value={form.spent_on}
              onChange={(e) => set('spent_on', e.target.value)}
            />
          </Field>

          <Field id="expense-note" label="Note (optional)" error={errors.note}>
            <textarea
              id="expense-note"
              rows={3}
              maxLength={200}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </Field>

          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}

          {expense && confirmDelete ? (
            // Asked once, in place of Save: the final confirm is the only
            // solid red, with the way back below it (docs/25).
            <div className="expense-confirm-delete sheet-stack" role="alert">
              <p>Delete this expense?</p>
              <button type="button" className="btn btn-danger-solid btn-block" onClick={remove} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete expense'}
              </button>
              <button type="button" className="btn btn-secondary btn-block" onClick={() => setConfirmDelete(false)} disabled={busy}>
                Keep expense
              </button>
            </div>
          ) : (
            <>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save expense'}
              </button>

              {expense && (
                <button type="button" className="btn btn-danger btn-compact" onClick={() => setConfirmDelete(true)} disabled={busy}>
                  Delete
                </button>
              )}
            </>
          )}
        </form>

        <button type="button" className="btn btn-quiet btn-block" onClick={onClose} disabled={busy}>
          Close
        </button>
      </div>
    </div>
  )

  // The sheet renders into the app shell rather than into the scrolling
  // screen. The tab bar carries a backdrop-filter, which makes it its own
  // stacking context and paints it over anything nested inside the screen —
  // no z-index on the overlay can win. Mounted here it sits above the tab bar
  // and the floating button, the same as every other sheet, and its actions
  // stay tappable instead of the nav swallowing the taps.
  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
